// =============================================================================
// PRESENCE RUN STORE — W23 (docs/WALK-FINDINGS-2026-07-21.md; the owner's word
// `Go-longops`, 2026-07-24). The in-flight Get Found generation's runId, kept
// where a remount can find it.
//
// WHY IT EXISTS: POST /presence/generate now fast-acks with a runId and does
// the real work server-side (apps/api/routes/presence.routes.ts). That fixes
// the timeout, but it leaves one honest gap — a user who navigates away, or
// whose mobile browser re-launches the SPA shell when they come back to a
// backgrounded tab, returns to a screen that has forgotten a run which is
// still going (or has since finished and BANKED its profile). Without this,
// the screen would show idle, and his only clue that a minute of work
// happened would be a new row quietly appearing in his saved versions.
//
// Mirrors jobsInboxCache.ts's two-tier discipline exactly — an in-memory
// module variable for the fast path within one page load, sessionStorage
// behind it for an actual reload in the same tab. Tab/session-scoped by
// construction: never localStorage, never shared across tabs or devices,
// never outliving the browser session. Kept OUT of any hook (a plain data
// module, no React) so it is directly unit-testable — this repo's vitest run
// has no jsdom, so a hook cannot be rendered in a test; a plain module can.
//
// It stores an ID and a platform string, never generated copy: the profile
// itself lives in presence_profiles, banked by the server before it answers.
// =============================================================================

export interface InFlightPresenceRun {
  /** The cron_runs id POST /presence/generate acked with. */
  runId: string;
  /** Which platform the run targets — so a resume only rejoins a run that
   *  matches the platform the screen is currently showing, never silently
   *  serves a LinkedIn profile into a Seek-selected screen. */
  platform: string;
  /** Epoch ms this run was started, client-side. Display/debug only — never
   *  used to expire the entry, since only the SERVER knows whether a run is
   *  still going, and guessing from the clock is exactly the invention this
   *  lane exists to remove. */
  startedAt: number;
}

const STORAGE_KEY = 'tl-presence-run';

let memoryRun: InFlightPresenceRun | null = null;

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function isValidEntry(value: unknown): value is InFlightPresenceRun {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<InFlightPresenceRun>;
  return (
    typeof v.runId === 'string' &&
    v.runId.length > 0 &&
    typeof v.platform === 'string' &&
    typeof v.startedAt === 'number'
  );
}

/** The in-flight run for THIS platform, or null. A run recorded against a
 *  different platform is not returned — it is another screen's business, and
 *  rejoining it would render copy the user did not ask for here. */
export function readPresenceRun(platform: string | null): InFlightPresenceRun | null {
  if (!platform) return null;
  if (memoryRun) return memoryRun.platform === platform ? memoryRun : null;
  if (!hasSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidEntry(parsed)) return null;
    memoryRun = parsed;
    return parsed.platform === platform ? parsed : null;
  } catch {
    return null;
  }
}

/** Records the run the instant POST /presence/generate acks — before any of
 *  the waiting happens, which is the only ordering that survives the user
 *  navigating away one second later. */
export function writePresenceRun(runId: string, platform: string): void {
  const entry: InFlightPresenceRun = { runId, platform, startedAt: Date.now() };
  memoryRun = entry;
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Best-effort only (private-browsing quota, storage disabled) — the
    // in-memory tier still covers this page load either way.
  }
}

/** Called when a run reaches a TERMINAL state — served, blocked, or a real
 *  server-side error. Deliberately NOT called when the client-side poll
 *  ceiling is hit: that run is still going, and forgetting it is precisely
 *  how completed work would go unnoticed again. */
export function clearPresenceRun(): void {
  memoryRun = null;
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort, same posture as the write above
  }
}
